import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const SubServiceItem = ({ service, index, activeIndex, setActiveIndex }) => {
  const [isMobile, setIsMobile] = useState(false);
  const isActive = activeIndex === index;
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Measure content height on mount and when content changes
  useEffect(() => {
    const updateHeight = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [service.fullDescription]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkMobile, 100);
    };
    
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  const handleMouseEnter = () => !isMobile && setActiveIndex(index);
  const handleMouseLeave = () => !isMobile && setActiveIndex(null);
  const handleClick = () => isMobile && setActiveIndex(isActive ? null : index);

  // Super smooth spring animation for the card
  const cardSpring = {
    type: "spring",
    stiffness: 200,
    damping: 25,
    mass: 1,
    restDelta: 0.001,
    restSpeed: 0.001
  };

  return (
    <motion.div
      className="relative cursor-pointer will-change-transform"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
    >
      <motion.div
        className="border border-green-500/30 rounded-xl bg-gray-900/80 p-6 relative overflow-hidden transform-gpu"
        animate={{
          scale: isActive && !isMobile ? 1.05 : 1,
          y: isActive ? -4 : 0,
          background: isActive
            ? "linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.05))"
            : "rgba(17, 24, 39, 0.8)",
          boxShadow: isActive && !isMobile
            ? "0px 6px 30px rgba(16, 185, 129, 0.6)"
            : "none",
          filter: !isActive && activeIndex !== null ? "brightness(0.7)" : "brightness(1)",
        }}
        transition={cardSpring}
        style={{
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden"
        }}
      >
        <motion.div 
          className="relative z-10 space-y-4 transform-gpu"
          layout
        >
          <motion.div 
            className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center"
            layout
          >
            {service.icon}
          </motion.div>

          <motion.h3 
            className="text-xl font-mono font-bold"
            layout
          >
            {service.title}
          </motion.h3>

          <motion.p 
            className="text-green-300/80 text-sm md:text-base leading-relaxed"
            layout
          >
            {service.description}
          </motion.p>

          <motion.div
            className="overflow-hidden"
            animate={{
              height: isActive ? contentHeight : 0,
              opacity: isActive ? 1 : 0,
              marginTop: isActive ? 16 : 0
            }}
            transition={{
              height: {
                type: "spring",
                stiffness: 150,
                damping: 20,
                mass: 0.8,
                restDelta: 0.001
              },
              opacity: {
                duration: 0.25,
                ease: "easeInOut",
                delay: isActive ? 0.1 : 0
              },
              marginTop: {
                type: "spring",
                stiffness: 150,
                damping: 20
              }
            }}
          >
            <div ref={contentRef}>
              <p className="text-green-200 text-sm leading-relaxed">
                {service.fullDescription}
              </p>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default SubServiceItem;